import React, { useState } from 'react';

import { sortBy } from 'lodash';
import moment from 'moment-timezone';
import { Controller, useForm } from 'react-hook-form';
import { isValidFileSize, isValidFileType, isValidUrl } from 'utilitys/helpers/general';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useQueryGetCustomerUsersList } from 'api/queries/getCustomerUsersList.query';

import { CustomizedDatePicker } from 'components/common/CustomizedDatePicker/CustomizedDatePicker';
import Icon from 'components/common/Icons/base/Icon/Icon';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import SelectWithCreate from 'components/common/SelectWithCreate/SelectWithCreate';
import { useSelectWithCreateHook } from 'components/common/SelectWithCreate/SelectWithCreateHook';
import RichTextArea from 'components/common/richTextArea/richTextArea';

import classes from 'components/Dashboards/Project/Components/DataStories/AddStoryForm/AddStoryForm.module.scss';
import { Radio } from 'components/DesignSystem/Radio';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

const userMentionMapper = (i) => ({
    text: `${i.firstName} ${i.lastName}`,
    value: `${i.firstName} ${i.lastName}`,
    url: i.userName,
});

interface IAddStoryContainerForm {
    id: number;
    contractId: number;
    contractName: string;
    createdDate: number;
    description: string;
    embeddedCode: string;
    file: File | { name: string } | null;
    mentionedList: string[];
    projectId: number;
    tagList: ISelectOption<string>[];
    title: string;
}

const AddStoryForm = ({ saveStory, project, contract, tags, editData, isAdmin }) => {
    const {
        handleSubmit,
        register,
        control,
        formState: { errors },
        setValue,
        watch,
        trigger,
    } = useForm<IAddStoryContainerForm>({
        defaultValues: {
            id: editData !== null ? editData.id : -1,
            projectId: project !== null ? project.id : '-1',
            contractId: contract !== null ? contract.id : '-1',
            contractName: contract !== null ? contract.title : 'All',
            title: editData !== null ? editData.title : '',
            description: editData !== null ? editData.description : '',
            mentionedList: editData !== null ? editData.mentionedList : [],
            embeddedCode: '',
            file: editData !== null ? { name: editData.file } : null,
            tagList: editData !== null ? editData.tagslist.map((item) => ({ label: item.name, value: item.name })) : [],
            createdDate: editData !== null ? editData.createdDate : moment().valueOf(),
        },
    });

    const fileName = watch('file');

    const { sendEventWithDimensions } = useEventWithDimensions();

    const [radioChecked, setRadioChecked] = useState('image');
    const { data: customerUsersList, isLoading } = useQueryGetCustomerUsersList();

    const handleEditorDescription = (editorState, mentionedList) => {
        setValue('description', editorState);
        setValue('mentionedList', mentionedList);
    };

    const handleRadioChecked = (e) => {
        setRadioChecked(e.target.value);
    };

    const handleChosenStartDate = (value) => {
        setValue('createdDate', moment(value).valueOf());
    };

    const controlProps = (item) => ({
        checked: radioChecked === item,
        onChange: handleRadioChecked,
        value: item,
        name: 'testRadioBtn',
    });

    const submitFrom = (value) => {
        const payload = {
            ...value,
            title: value.title.trim(),
            tagList: value.tagList.map((item) => ({ name: item.value })),
        };
        saveStory(payload);

        sendEventWithDimensions({
            category: 'Data-Story',
            action: editData ? 'Update' : 'Create',
            label: value.id,
        });
    };

    const embeddedCodeValidation = (value) => {
        if (radioChecked !== 'embeddedCode' || value.length === 0) {
            return true;
        }

        return isValidUrl(value);
    };

    const handleImageSelection = (e) => {
        setValue('file', e.target.files[0]);
        return trigger('file');
    };

    const tagsOptions = tags ? tags.map((item) => ({ label: item.name, value: item.name })) : [];

    const { inputValue, onInputChange, onChange } = useSelectWithCreateHook({
        onChangeCallback: (value) =>
            setValue(
                'tagList',
                value.map((item) => ({ label: item.value.trim(), value: item.value.trim() })),
                { shouldDirty: true },
            ),
    });

    return (
        <div className={classes.addStoryContainer}>
            {isLoading && <OverlayWithSpinner />}
            <form onSubmit={handleSubmit(submitFrom)} id={'add-data-story-form'}>
                <div className={classes.wrapper}>
                    {!project && !contract ? (
                        <div className={classes.content}>
                            <span className={classes.title}>Portfolio</span>
                        </div>
                    ) : (
                        <>
                            <div className={classes.content}>
                                <span className={classes.title}>Project*</span>
                                <span className={classes.contentValue}>{project.title || 'All'}</span>
                            </div>
                            <div className={classes.content}>
                                <span className={classes.title}>Sub-Program*</span>
                                <span className={classes.contentValue}>{contract?.title || 'All'}</span>
                            </div>
                        </>
                    )}

                    <div className={classes.content}>
                        <span className={classes.title}>Date*</span>
                        <div className={classes.dateInputWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <CustomizedDatePicker
                                        value={field.value}
                                        handleChange={handleChosenStartDate}
                                        minDate={moment().subtract(20, 'year').valueOf()}
                                    />
                                )}
                                name={'createdDate'}
                                control={control}
                            />
                        </div>
                    </div>

                    <div className={classes.content}>
                        <span className={classes.title}>Title*</span>
                        <div className={classes.contentValueWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        onClear={() => {
                                            setValue('title', '');
                                        }}
                                        id={'story-title'}
                                        placeholder={'Enter Title'}
                                        maxLength={200}
                                        className={classes.inputContentValue}
                                    />
                                )}
                                name={'title'}
                                control={control}
                                rules={{
                                    required: 'Title is Required',
                                    minLength: { value: 2, message: 'Title is to short' },
                                }}
                            />
                            {errors.title && <CustomizedFormErrorMessage text={errors.title.message} />}
                        </div>
                    </div>
                    <div className={classes.content}>
                        <span className={`${classes.description} ${classes.title}`}>Description:</span>
                        <div className={`${classes.descriptionTextAreaWrapper} ${classes.contentValue}`}>
                            <Controller
                                render={() => (
                                    <RichTextArea
                                        onEditorDescription={handleEditorDescription}
                                        existingData={editData !== null ? editData.description : ''}
                                        editorContentHeight={220}
                                        mention={{
                                            separator: ' ',
                                            trigger: '@',
                                            suggestions:
                                                sortBy(customerUsersList?.map(userMentionMapper), 'text') || [],
                                        }}
                                    />
                                )}
                                name={'description'}
                                control={control}
                            />
                        </div>
                    </div>

                    {isAdmin && (
                        <div className={classes.content}>
                            <span className={classes.title}>
                                <Radio {...controlProps('embeddedCode')} label={'External BI URL'} />
                            </span>

                            <div className={classes.contentValueWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            onClear={() => {
                                                setValue('embeddedCode', '');
                                            }}
                                            id={'story-embeddedCode'}
                                            placeholder={''}
                                            maxLength={200}
                                            className={classes.inputContentValue}
                                            disabled={radioChecked !== 'embeddedCode'}
                                        />
                                    )}
                                    name={'embeddedCode'}
                                    control={control}
                                    rules={{
                                        validate: (value) => embeddedCodeValidation(value) || 'Invalid URL',
                                    }}
                                />
                                {errors.embeddedCode && (
                                    <CustomizedFormErrorMessage text={errors.embeddedCode.message} />
                                )}
                            </div>
                        </div>
                    )}

                    <div className={classes.content}>
                        <span className={classes.title}>
                            <Radio {...controlProps('image')} label={'Image'} />
                        </span>
                        <label htmlFor={'image-upload'} className={classes.customImageUpload}>
                            <Icon
                                name={'Download-icon'}
                                size={'20'}
                                color={
                                    radioChecked === 'image' ? 'var(--text-brand-primary)' : 'var(--text-quaternary)'
                                }
                            />
                            <span
                                className={classes.imageUploadTitle}
                                style={{
                                    color:
                                        radioChecked === 'image'
                                            ? 'var(--text-brand-primary)'
                                            : 'var(--text-quaternary)',
                                }}
                            >
                                Upload Image
                            </span>

                            {errors.file ? (
                                <CustomizedFormErrorMessage text={errors.file.message} textAlign={'right'} />
                            ) : (
                                <span className={classes.imageUploadTitle}>{fileName && fileName.name}</span>
                            )}
                        </label>
                        <input
                            id={'image-upload'}
                            type={'file'}
                            accept={'image/png, image/jpeg, image/gif'}
                            disabled={radioChecked !== 'image'}
                            {...register('file', {
                                validate: {
                                    isValidFileType: (value) =>
                                        value === null ||
                                        value.name === null ||
                                        isValidFileType(value, ['image/gif', 'image/jpeg', 'image/png']) ||
                                        'Only jpg/jpeg/png and gif files are allowed!',
                                    isValidFileSize: (value) =>
                                        value === null ||
                                        value.name === null ||
                                        isValidFileSize(value, 5242880) ||
                                        'Maximum file size allowed for upload 5mb',
                                },
                            })}
                            onChange={handleImageSelection}
                        />
                    </div>
                    <div className={classes.content}>
                        <span className={classes.title}>Tags:</span>
                        <div className={classes.contentValue}>
                            <Controller
                                render={({ field }) => (
                                    <SelectWithCreate
                                        {...field}
                                        options={tagsOptions}
                                        id={'story-tagslist'}
                                        isMulti={true}
                                        isClearable={true}
                                        inputValue={inputValue}
                                        onInputChange={onInputChange}
                                        onChange={onChange}
                                    />
                                )}
                                name={'tagList'}
                                control={control}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AddStoryForm;
